import React, { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'

import { Form, Input, Button, Checkbox, Select, Divider, Steps } from 'antd'
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  MobileOutlined,
  CodeSandboxOutlined,
  GoogleOutlined,
  GithubOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  HomeOutlined
} from '@ant-design/icons'
import { register } from '../../reducers/authReducer'

import { useDispatch } from 'react-redux'

import {
  FormItemFlex,
  PageContainer,
  FormContainer,
  FormWrapper,
  FormTitle,
  FormSubtitle,
  StyledForm,
  StepsContainer,
  ButtonContainer
} from './style'

const { Step } = Steps

const Registeration = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(0)
  const [formValues, setFormValues] = useState({})

  const steps = [
    {
      title: 'Personal Info',
      content: 'personal'
    },
    {
      title: 'Account Details',
      content: 'account'
    },
    {
      title: 'Security',
      content: 'security'
    },
    {
      title: 'Complete',
      content: 'complete'
    }
  ]

  const onFinish = async () => {
    try {
      // Get all form values
      const allValues = await form.validateFields()
      console.log('Received values of form: ', allValues)
      // registration succeeds only if backend returns 201; otherwise register() throws
      await dispatch(register(allValues))
      history.push('/login')
    } catch (e) {
      console.log("can't register", e)
    }
  }

  const next = async () => {
    try {
      // Validate current step fields
      const fieldsToValidate = getFieldsForStep(currentStep)
      const values = await form.validateFields(fieldsToValidate)

      // Store values and update form
      const updatedValues = { ...formValues, ...values }
      setFormValues(updatedValues)
      form.setFieldsValue(updatedValues)

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1)
      }
    } catch (errorInfo) {
      console.log('Validation failed:', errorInfo)
    }
  }

  const prev = () => {
    if (currentStep > 0) {
      // Save current step values before going back
      const currentValues = form.getFieldsValue()
      const updatedValues = { ...formValues, ...currentValues }
      setFormValues(updatedValues)
      form.setFieldsValue(updatedValues)
      setCurrentStep(currentStep - 1)
    }
  }

  // Update form values when step changes
  useEffect(() => {
    if (Object.keys(formValues).length > 0) {
      form.setFieldsValue(formValues)
    }
  }, [currentStep, form, formValues])

  const getFieldsForStep = (step) => {
    switch (step) {
      case 0: // Personal Info
        return ['name', 'username', 'email', 'role']
      case 1: // Account Details
        return ['mobile', 'code']
      case 2: // Security
        return ['password', 'passwordConfirm', 'agreement']
      default:
        return []
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Personal Info
        return (
          <>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: 'please enter your name'
                },
                {
                  min: 3,
                  message: 'please min length for name is 3'
                }
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Full Name" />
            </Form.Item>

            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: 'please enter your username'
                },
                {
                  min: 3,
                  message: 'please min length for username is 3'
                }
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Username" />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                {
                  type: 'email',
                  message: 'The input is not valid E-mail!'
                },
                {
                  required: true,
                  message: 'Please input your E-mail!'
                }
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="E-Mail" />
            </Form.Item>

            <Form.Item
              name="role"
              rules={[
                {
                  required: true,
                  message: 'Please select your role!'
                }
              ]}
            >
              <Select placeholder="Select your role">
                <Select.Option value="instructor">Instructor</Select.Option>
                <Select.Option value="student">Student</Select.Option>
              </Select>
            </Form.Item>
          </>
        )

      case 1: // Account Details
        return (
          <>
            <Form.Item
              name="mobile"
              rules={[
                {
                  required: true,
                  message: 'Please input your phone!'
                },
                {
                  pattern: /^\d{3}-\d{3}-\d{4}$/,
                  message: 'Phone must be in format 123-456-7890'
                }
              ]}
            >
              <Input
                prefix={<MobileOutlined />}
                placeholder="Mobile (123-456-7890)"
              />
            </Form.Item>

            <Form.Item
              name="code"
              rules={[
                {
                  required: true,
                  message: 'Please input your code!',
                  whitespace: true
                },
                {
                  len: 6,
                  message: 'Code must be exactly 6 characters'
                }
              ]}
            >
              <Input prefix={<CodeSandboxOutlined />} placeholder="Code (6 characters)" maxLength={6} />
            </Form.Item>
          </>
        )

      case 2: // Security
        return (
          <>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please input your password!'
                },
                {
                  min: 7,
                  message: 'Password must be at least 7 characters'
                }
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
              />
            </Form.Item>

            <Form.Item
              name="passwordConfirm"
              dependencies={['password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password!'
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }

                    return Promise.reject(
                      'The two passwords that you entered do not match!'
                    )
                  }
                })
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm Password"
              />
            </Form.Item>

            <FormItemFlex>
              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject('Should accept agreement')
                  }
                ]}
              >
                <Checkbox>
                  I have read the <Link to="#">agreement</Link>
                </Checkbox>
              </Form.Item>
            </FormItemFlex>
          </>
        )

      case 3: // Complete
        return (
          <>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✓</div>
              <h3 style={{ color: 'white', marginBottom: '10px' }}>Review Your Information</h3>
              <p style={{ color: '#9CA3AF', marginBottom: '30px' }}>
                Please review your information before submitting
              </p>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'left',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#9CA3AF', margin: '8px 0' }}><strong style={{ color: 'white' }}>Name:</strong> {formValues.name}</p>
                <p style={{ color: '#9CA3AF', margin: '8px 0' }}><strong style={{ color: 'white' }}>Username:</strong> {formValues.username}</p>
                <p style={{ color: '#9CA3AF', margin: '8px 0' }}><strong style={{ color: 'white' }}>Email:</strong> {formValues.email}</p>
                <p style={{ color: '#9CA3AF', margin: '8px 0' }}><strong style={{ color: 'white' }}>Role:</strong> {formValues.role === 'instructor' ? 'Instructor' : 'Student'}</p>
                <p style={{ color: '#9CA3AF', margin: '8px 0' }}><strong style={{ color: 'white' }}>Mobile:</strong> {formValues.mobile}</p>
              </div>
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <PageContainer>
      <FormContainer>
        <FormWrapper>
          <FormTitle>Create Account</FormTitle>
          <FormSubtitle>Join us to create and manage your learning experience</FormSubtitle>
          <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Already have an account? <Link to="/login" style={{ color: 'hsl(33, 100%, 50%)', textDecoration: 'none' }}>Sign in</Link>
            <span style={{ margin: '0 12px', color: '#4B5563' }}>|</span>
            <Link to="/" style={{ color: '#9CA3AF', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <HomeOutlined /> Go Home
            </Link>
          </p>

          <StepsContainer>
            <Steps current={currentStep} size="small">
              {steps.map(item => (
                <Step key={item.title} title={item.title} />
              ))}
            </Steps>
          </StepsContainer>

          <StyledForm
            form={form}
            name="register"
            initialValues={formValues}
            scrollToFirstError
            layout="vertical"
          >
            {renderStepContent()}

            {currentStep === 3 ? (
              <ButtonContainer>
                <Button
                  type="default"
                  icon={<ArrowLeftOutlined />}
                  onClick={prev}
                  size="large"
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  onClick={onFinish}
                  size="large"
                  style={{ flex: 1 }}
                >
                  Submit Registration
                </Button>
              </ButtonContainer>
            ) : (
              <ButtonContainer>
                {currentStep > 0 && (
                  <Button
                    type="default"
                    icon={<ArrowLeftOutlined />}
                    onClick={prev}
                    size="large"
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={next}
                  size="large"
                  style={{ flex: 1, marginLeft: currentStep > 0 ? '12px' : '0' }}
                >
                  Next
                </Button>
              </ButtonContainer>
            )}

            {currentStep === 0 && (
              <>
                <Divider plain>Or</Divider>

                <Form.Item style={{ marginBottom: '8px' }}>
                  <Button
                    block
                    icon={<img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" alt="Google" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />}
                    onClick={() => {
                      const backendUrl = process.env.REACT_APP_API_URL || 'https://wiseacademybackend.vercel.app'
                      window.location.href = `${backendUrl}/users/auth/google`
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Sign up with Google
                  </Button>
                </Form.Item>

                <Form.Item style={{ marginBottom: '0' }}>
                  <Button
                    block
                    icon={<img src="https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png" alt="GitHub" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />}
                    onClick={() => {
                      const backendUrl = process.env.REACT_APP_API_URL || 'https://wiseacademybackend.vercel.app'
                      window.location.href = `${backendUrl}/users/auth/github`
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Sign up with GitHub
                  </Button>
                </Form.Item>
              </>
            )}
          </StyledForm>
        </FormWrapper>
      </FormContainer>
    </PageContainer>
  )
}

export default Registeration

